"use client";

import { Toast as ToastPrimitive } from "@base-ui/react/toast";
import * as React from "react";
import { LuCircleCheck, LuPlay, LuTriangleAlert } from "react-icons/lu";

import CopyToClipboard from "@/components/shared/copy-to-clipboard";
import { Button } from "@/components/ui/button";
import {
  Toast,
  ToastClose,
  ToastContent,
  ToastDescription,
  ToastPortal,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast";
import { toastManager } from "@/components/ui/use-toast";

export function Toaster() {
  return (
    <ToastProvider limit={4} toastManager={toastManager}>
      <ToastPortal>
        <ToastViewport>
          <ToastList />
        </ToastViewport>
      </ToastPortal>
    </ToastProvider>
  );
}

function ToastList() {
  const { toasts } = ToastPrimitive.useToastManager<{
    action?: React.ReactElement;
    copyText?: string;
    details?: React.ReactNode;
    icon?: React.ReactNode;
  }>();

  return toasts.map((toast) => {
    const icon = getToastIcon(toast);
    const hasExtraContent =
      Boolean(toast.data?.details) ||
      Boolean(toast.type === "destructive" && toast.data?.copyText);

    return (
      <Toast key={toast.id} toast={toast}>
        <ToastContent
          className={
            toast.data?.action
              ? "grid-cols-[1fr_auto] items-start gap-3"
              : "grid-cols-1 items-start gap-3"
          }
        >
          <div className="grid min-w-0 gap-3">
            <div
              className={
                icon
                  ? "grid min-w-0 grid-cols-[auto_1fr] items-center gap-3"
                  : "grid min-w-0"
              }
            >
              {icon && <ToastIcon>{icon}</ToastIcon>}
              <div className="grid min-w-0">
                {toast.title && <ToastTitle />}
                {toast.description && <ToastDescription />}
              </div>
            </div>
            {hasExtraContent && (
              <ToastExtraContent
                copyText={
                  toast.type === "destructive"
                    ? toast.data?.copyText
                    : undefined
                }
                details={toast.data?.details}
                indent={Boolean(icon)}
              />
            )}
          </div>
          {toast.data?.action}
          <ToastClose />
        </ToastContent>
      </Toast>
    );
  });
}

const ToastIcon = ({ children }: { children: React.ReactNode }) => (
  <span
    aria-hidden="true"
    className="
      flex size-5 shrink-0 items-center justify-center self-center
      group-data-[type=destructive]:text-red-200 [&>svg]:size-5
    "
  >
    {children}
  </span>
);
ToastIcon.displayName = "ToastIcon";

const getToastIcon = (toast: {
  type?: string;
  data?: { icon?: React.ReactNode };
}) => {
  if (toast.data?.icon) {
    return toast.data.icon;
  }

  if (toast.type === "success") {
    return <LuCircleCheck />;
  }

  if (toast.type === "destructive") {
    return <LuTriangleAlert />;
  }

  return null;
};

const toastActionButtonClassName = `
  inline-flex h-7 w-fit cursor-pointer items-center gap-1.5 rounded-md
  bg-foreground/10 px-2.5 text-xs font-semibold text-foreground
  transition-colors
  hover:bg-foreground/15 hover:text-foreground aria-expanded:bg-foreground/15
  focus:ring-1 focus:ring-ring focus:outline-hidden
  group-data-[type=destructive]:bg-white/10
  group-data-[type=destructive]:text-red-50
  group-data-[type=destructive]:hover:bg-white/15
  group-data-[type=destructive]:hover:text-white
  group-data-[type=destructive]:aria-expanded:bg-white/15
  group-data-[type=destructive]:focus:ring-red-200/50
`;

const ToastExtraContent = ({
  copyText,
  details,
  indent,
}: {
  copyText?: string;
  details?: React.ReactNode;
  indent: boolean;
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const detailsId = React.useId();

  return (
    <div className={indent ? "grid min-w-0 gap-3 pl-8" : "grid min-w-0 gap-3"}>
      <div className="flex flex-wrap items-center gap-2">
        {details && (
          <button
            type="button"
            aria-controls={detailsId}
            aria-expanded={isOpen}
            className={toastActionButtonClassName}
            onClick={() => setIsOpen((current) => !current)}
          >
            <LuPlay
              className={`size-3 transition-transform duration-150 ${
                isOpen ? "rotate-90" : ""
              }`}
            />
            <span>Details</span>
          </button>
        )}
        {copyText && (
          <CopyToClipboard
            text={copyText}
            showToast={false}
            as={Button}
            size="xs"
            variant="ghost"
            className={toastActionButtonClassName}
            iconClassName="text-red-100"
          >
            Copy Error
          </CopyToClipboard>
        )}
      </div>
      {details && isOpen && (
        <div
          id={detailsId}
          className="
            max-h-64 overflow-auto rounded-md border border-foreground/15
            bg-foreground/5 px-3 py-2 text-[11px]/4 opacity-90
            group-data-[type=destructive]:border-red-300/25
            group-data-[type=destructive]:bg-black/20
            group-data-[type=destructive]:text-red-50/90
          "
        >
          {typeof details === "string" ? (
            <pre className="wrap-break-word whitespace-pre-wrap">{details}</pre>
          ) : (
            details
          )}
        </div>
      )}
    </div>
  );
};
ToastExtraContent.displayName = "ToastExtraContent";
